import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { getDocumentsList } from '../../utils/services/mailRoomAPI';
import Loader from '../../components/Loader';
import ScrollContainer from '../../components/ScrollContainerV2';
import { MailRoomContext } from '../../context/mailRoomContext';
import { Card, DataLoader } from './styles';
import { Empty } from 'antd';
import { dateTimeConversion } from '../../utils/javascript';
import { PDFFileIcon, PagesIcon } from '../../images/svgIcons';
import { RANDOM_COLORS } from '../../styles/variables';
import URL from '../../utils/helper';
import { withTranslation } from 'react-i18next';
import { BATCH_LIST } from './translationValues';

const NAMESPACES = ['BATCH_LIST'];

const DocumentList = props => {
  const {
    selectable,
    selectedDocIds,
    setSelectedDocIds,
    excludedDocIds,
    setExcludedDocIds,
    selectedAll,
    setSelectedAll,
    isCentered,
    isLoading,
    setIsLoading,
    pageLimit,
    t
  } = props;

  const [documentList, setDocumentList] = useState([]);
  const [fetchingMoreData, setFetchingMoreData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDrawer, setShowDrawer] = useState(false);
  const isMounted = useRef(true)

  const {
    searchByDocName,
    payloadTags,
    startAndEndDate,
    pageFilter,
    docsCount,
    setPagesCount,
    setDocsCount,
  } = useContext(MailRoomContext);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (selectable) {
      setSelectedDocIds([]);
      setExcludedDocIds([]);
    }
    if (startAndEndDate.startDate && startAndEndDate.endDate) {
      setCurrentPage(1);
      setIsLoading(true);
      fetchDocumentList(1);
    }
  }, [startAndEndDate, searchByDocName, payloadTags, pageFilter]);

  useEffect(() => {
    if (currentPage > 1) {
      setFetchingMoreData(true);
      fetchDocumentList(currentPage);
    }
  }, [currentPage]);

  const fetchMoreData = useCallback(() => {
    if (docsCount > currentPage * pageLimit) {
      setCurrentPage(currentPage => currentPage + 1);
    }
  }, [currentPage, docsCount]);

  const fetchDocumentList = async page => {
    let payload = { page: page, limit: pageLimit };
    let dateRange = startAndEndDate;
    if (dateRange.startDate && dateRange.endDate) {
      payload.createdOnStartTime = dateRange.startDate;
      payload.createdOnEndTime = dateRange.endDate;
    }
    if (searchByDocName) {
      payload.docName = searchByDocName;
    }
    if(pageFilter) {
      payload.documentPages = parseInt(pageFilter);
    }
    const response = await getDocumentsList(payload, payloadTags);
    if (response && isMounted.current) {
      if (response.errors && response.errors.length === 0) {
        if (response && response.data && response?.data?.data) {
          let allDocuments;
          if (page && page > 1) {
            allDocuments = documentList?.concat(response?.data?.data);
          } else {
            allDocuments = response?.data?.data;
          }
          setDocumentList(allDocuments);
          setDocsCount(response.dataCount);
          setPagesCount(response.data.pages ? response.data.pages : 0);
        }
      }
      setIsLoading(false);
      setFetchingMoreData(false);
    }
  };

  const onDocSelect = (e, doc) => {
    let selected = [];
    let unselected = [];
    if (selectedAll) {
      if (excludedDocIds.includes(doc.id)) {
        unselected = excludedDocIds.filter(item => item !== doc.id);
      } else {
        unselected = [doc.id, ...excludedDocIds];
      }
      docsCount === unselected.length ? setSelectedAll(false) : setSelectedAll(true);
    } else {
      if (selectedDocIds.includes(doc.id)) {
        selected = selectedDocIds.filter(item => item !== doc.id);
      } else {
        selected = [doc.id, ...selectedDocIds];
      }
      docsCount === selected.length ? setSelectedAll(true) : setSelectedAll(false);
    }
    setSelectedDocIds(selected);
    setExcludedDocIds(unselected);
  };

  const storageLink = (link) => URL.TRANSACTION_SYSTEM_URL + link?.slice(1);

  return (
    <ScrollContainer
      customClassName={`batchList customHeight ${isCentered && 'flexCenter'} ${showDrawer ? 'collapse' : 'expand'
        }`}
      fetchMoreData={fetchMoreData}
    >
      {isLoading ? (
        <Loader />
      ) : documentList?.length > 0 ? (
        <>
          {documentList.map((doc, key) => {
            return (
              <Card
                height={'106px'}
                width={'300px'}
                borderRadius={'10px'}
                key={key}
                animation={true}
                animationDelay={key <= pageLimit - 1 ? key * 200 + 'ms' : ''}
                randomColor={RANDOM_COLORS[key % RANDOM_COLORS.length]}
                onClick={e =>
                  selectable ? onDocSelect(e, doc) : window.open(storageLink(doc.storageLink), '_blank')
                }
                style={{
                  outline: selectable && ((selectedAll && !excludedDocIds?.includes(doc.id)) ||
                    selectedDocIds?.includes(doc.id)) ? '2px solid red' : 'none'
                }}
                cursorPointer={true}
              >
                <div className="styledDiv">{PDFFileIcon()}</div>
                <div className="textContainer">
                  <p className="date">{dateTimeConversion(doc.createdOn, true, true, false)}</p>
                  <div className="docName">
                    <p className="name">
                      <a href={storageLink(doc.storageLink)} target="blank" onClick={e => e.stopPropagation()}>
                        {doc.docName}.pdf
                      </a>
                    </p>
                  </div>
                  <div className="pageCount">
                    {PagesIcon()}
                    <p>{doc.docPages}</p>
                  </div>
                </div>
              </Card>
            );
          })}
          <DataLoader>
            {fetchingMoreData
              ? t(BATCH_LIST.LOADING.key, BATCH_LIST.LOADING.value)
              : documentList?.length === docsCount && t(BATCH_LIST.GOT_ALL_RESULT_MESSAGE.key, BATCH_LIST.GOT_ALL_RESULT_MESSAGE.value)}
          </DataLoader>
        </>
      ) : (
        <Empty description={t(BATCH_LIST.NO_DOCUMENT_AVAILABLE_DESCRIPTION.key, BATCH_LIST.NO_DOCUMENT_AVAILABLE_DESCRIPTION.value)} />
      )}
    </ScrollContainer>
  );
};

export default withTranslation(NAMESPACES)(DocumentList);
